import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Check, MessageSquare, Play, Plug, Plus, RefreshCw, Send } from 'lucide-react';
import {
  PERMISSIONS,
  WATI_FIELD_SOURCES,
  WATI_FIELD_SOURCE_LABEL,
  type EvolutionConfigView,
  type MessageProvider,
  type WatiFieldSource,
  type WatiTemplate,
  type WatiVariableMapping,
} from '@pms/shared';
import { messageService, type WatiConfigResponse } from '@/services/message.service';
import { getErrorMessage } from '@/lib/errors';
import { usePermission } from '@/hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchInput } from '@/components/ui/search-input';
import { Switch } from '@/components/ui/switch';
import { Tabs } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { EvolutionInstanceDialog } from './EvolutionInstanceDialog';
import { useMessageConfig } from './useMessageConfig';

/** Rows rendered before the user searches. The tenant has 300+ approved templates. */
const TEMPLATE_LIST_CAP = 60;

/** Sample values behind the WhatsApp Direct preview, mirroring the server's test recipient. */
const PREVIEW_VALUES: Record<WatiFieldSource, string> = {
  employee_name:    'Test Employee',
  employee_code:    'EMP001',
  email:            'test@example.com',
  phone:            '9876543210',
  department_name:  'Sample Department',
  designation_name: 'Sample Designation',
  date_ist:         new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10),
  first_punch_in:   '9:42 AM',
  punch_count:      '1',
};

/** Renders a message body with its {{placeholders}} picked out. */
function BodyPreview({ body }: { body: string }) {
  const parts = body.split(/(\{\{\s*[^{}]+?\s*\}\})/g);
  return (
    <pre className="max-h-28 overflow-y-auto whitespace-pre-wrap rounded-md border border-border/60 bg-muted/30 p-2 text-xs leading-relaxed">
      {parts.map((part, i) =>
        /^\{\{/.test(part) ? (
          <span key={i} className="rounded bg-primary/15 px-1 font-semibold text-primary">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </pre>
  );
}

/** Compact label + description + control row, the shape used across HRMS Config. */
function SettingRow({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </div>
  );
}

function TemplatePicker({
  templates,
  selected,
  onSelect,
  disabled,
}: {
  templates: WatiTemplate[];
  selected: string | null;
  onSelect: (t: WatiTemplate) => void;
  disabled: boolean;
}) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const matches = useMemo(
    () => (needle === '' ? templates : templates.filter((t) => t.name.toLowerCase().includes(needle))),
    [templates, needle],
  );
  const shown = matches.slice(0, TEMPLATE_LIST_CAP);

  return (
    <div className="space-y-2">
      <SearchInput
        value={q}
        onChange={setQ}
        disabled={disabled}
        placeholder={`Search ${templates.length} approved templates…`}
      />
      <div className="max-h-52 overflow-y-auto rounded-md border border-border/60">
        {shown.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">No template matches “{q}”.</p>
        ) : (
          shown.map((t) => (
            <button
              key={t.name}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(t)}
              className={cn(
                'flex w-full items-center gap-2 border-b border-border/40 px-2.5 py-1.5 text-left text-sm last:border-b-0 transition-colors',
                t.name === selected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/40',
              )}
            >
              {t.name === selected ? (
                <Check className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              <span className="flex-1 truncate font-medium">{t.name}</span>
              {t.category && (
                <Badge variant="secondary" className="shrink-0 text-[10px]">{t.category}</Badge>
              )}
            </button>
          ))
        )}
      </div>
      {matches.length > TEMPLATE_LIST_CAP && (
        <p className="text-[11px] text-muted-foreground">
          Showing {TEMPLATE_LIST_CAP} of {matches.length} — keep typing to narrow.
        </p>
      )}
    </div>
  );
}

/** Local mirror of evolution_config, minus the write-only API key. */
type EvolutionDraft = Omit<EvolutionConfigView, 'api_key_set' | 'api_key_last4'>;

function WatiPanel({
  initial,
  canEdit,
  templateName,
  setTemplateName,
  mappings,
  setMappings,
  params,
  everyParamMapped,
  enabled,
}: {
  initial: WatiConfigResponse;
  canEdit: boolean;
  templateName: string | null;
  setTemplateName: (v: string | null) => void;
  mappings: WatiVariableMapping[];
  setMappings: (updater: (prev: WatiVariableMapping[]) => WatiVariableMapping[]) => void;
  params: { name: string; sample: string | null }[];
  everyParamMapped: boolean;
  enabled: boolean;
}) {
  const queryClient = useQueryClient();

  const templatesQuery = useQuery({
    queryKey: ['messages', 'templates'],
    queryFn: () => messageService.listTemplates(),
    enabled: canEdit && initial.is_configured,
  });

  const selectedTemplate = templatesQuery.data?.find((t) => t.name === templateName) ?? null;

  function chooseTemplate(t: WatiTemplate) {
    setTemplateName(t.name);
    // Variables belong to a template — carry over a mapping only where the param name
    // still exists, so switching template can't smuggle the old one's bindings through.
    setMappings((prev) =>
      t.params.map(
        (p) =>
          prev.find((m) => m.param_name === p.name) ?? {
            param_name: p.name,
            source_type: 'field' as const,
            source_field: 'employee_name' as WatiFieldSource,
            static_value: null,
          },
      ),
    );
  }

  function setMapping(paramName: string, patch: Partial<WatiVariableMapping>) {
    setMappings((prev) => prev.map((m) => (m.param_name === paramName ? { ...m, ...patch } : m)));
  }

  /** Bypasses the server's 10-minute cache, for a template just approved in WATI. */
  const refreshTemplates = useMutation({
    mutationFn: () => messageService.listTemplates(true),
    onSuccess: (rows) => {
      queryClient.setQueryData(['messages', 'templates'], rows);
      toast.success(`Loaded ${rows.length} approved templates`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-3">
      {!initial.is_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50/60 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-semibold">WATI credentials are missing on the server.</span>{' '}
            Set <code>WATI_BASE_URL</code>, <code>WATI_TENANT_ID</code> and{' '}
            <code>WATI_ACCESS_TOKEN</code> in the backend <code>.env</code> and restart. Settings
            below can be saved, but nothing will send.
          </p>
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-1">
            <CardTitle>Message template</CardTitle>
            {canEdit && initial.is_configured && (
              <Button
                variant="ghost"
                size="sm"
                disabled={refreshTemplates.isPending}
                onClick={() => refreshTemplates.mutate()}
              >
                <RefreshCw className={cn('h-3.5 w-3.5', refreshTemplates.isPending && 'animate-spin')} />
                Refresh
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {!initial.is_configured ? (
              <p className="text-sm text-muted-foreground">Configure credentials to load templates.</p>
            ) : templatesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading templates from WATI…</p>
            ) : templatesQuery.isError ? (
              <div className="space-y-2">
                <p className="text-sm text-destructive">{getErrorMessage(templatesQuery.error)}</p>
                <Button variant="outline" size="sm" onClick={() => void templatesQuery.refetch()}>
                  Retry
                </Button>
              </div>
            ) : (
              <TemplatePicker
                templates={templatesQuery.data ?? []}
                selected={templateName}
                onSelect={chooseTemplate}
                disabled={!canEdit}
              />
            )}

            {selectedTemplate && <BodyPreview body={selectedTemplate.body} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-1">
            <CardTitle>Variables</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {!templateName ? (
              <p className="text-sm text-muted-foreground">Pick a template to map its variables.</p>
            ) : params.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                This template has no variables — it sends the same text to everyone.
              </p>
            ) : (
              params.map((p) => {
                const m = mappings.find((x) => x.param_name === p.name);
                if (!m) return null;
                return (
                  <div
                    key={p.name}
                    className="flex flex-col gap-2 rounded-lg border border-border/60 px-3 py-2 sm:flex-row sm:items-center"
                  >
                    <div className="sm:w-28 sm:shrink-0">
                      <p className="font-mono text-xs font-semibold">{`{{${p.name}}}`}</p>
                      {p.sample && (
                        <p className="truncate text-[11px] text-muted-foreground">e.g. “{p.sample}”</p>
                      )}
                    </div>

                    <select
                      value={m.source_type === 'static' ? '__static__' : (m.source_field ?? '')}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setMapping(
                          p.name,
                          e.target.value === '__static__'
                            ? { source_type: 'static', source_field: null }
                            : {
                                source_type: 'field',
                                source_field: e.target.value as WatiFieldSource,
                                static_value: null,
                              },
                        )
                      }
                      className="h-9 min-w-0 flex-1 rounded-md border border-border/60 bg-background px-2 text-sm"
                    >
                      {WATI_FIELD_SOURCES.map((s) => (
                        <option key={s} value={s}>{WATI_FIELD_SOURCE_LABEL[s]}</option>
                      ))}
                      <option value="__static__">Custom text…</option>
                    </select>

                    {m.source_type === 'static' && (
                      <Input
                        value={m.static_value ?? ''}
                        onChange={(e) => setMapping(p.name, { static_value: e.target.value })}
                        placeholder="Text sent to everyone"
                        disabled={!canEdit}
                        className="h-9 min-w-0 flex-1 sm:h-9"
                      />
                    )}
                  </div>
                );
              })
            )}

            {enabled && templateName && !everyParamMapped && (
              <p className="text-xs text-destructive">
                Every variable needs a value before reminders can be switched on.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EvolutionPanel({
  initial,
  canEdit,
  draft,
  setDraft,
  apiKey,
  setApiKey,
}: {
  initial: WatiConfigResponse;
  canEdit: boolean;
  draft: EvolutionDraft;
  setDraft: (patch: Partial<EvolutionDraft>) => void;
  apiKey: string;
  setApiKey: (v: string) => void;
}) {
  const keyOnServer = initial.evolution.api_key_set;

  /**
   * Uncached and manual. This reflects a live WhatsApp session that the linked phone can
   * log out at any moment, so a stale "open" would be worse than no badge at all.
   */
  const statusQuery = useQuery({
    queryKey: ['messages', 'evolution', 'status'],
    queryFn: () => messageService.getEvolutionStatus(),
    enabled: canEdit && keyOnServer,
    retry: false,
  });

  const body = draft.message_body ?? '';

  function insertToken(token: string) {
    setDraft({ message_body: `${body}${token}` });
  }

  const state = statusQuery.data?.state ?? null;

  const [pairing, setPairing] = useState<'create' | 'repair' | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg border border-sky-300/60 bg-sky-50/60 px-3 py-2 text-xs text-sky-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          <span className="font-semibold">This is not the official WhatsApp API.</span> It drives a
          real WhatsApp account through a linked device, so it sends free text rather than approved
          templates — and unsolicited bursts can get the number banned. Keep the send delay generous
          and leave the WhatsApp check on.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-1">
            <CardTitle>Connection</CardTitle>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPairing('create')}
                  disabled={!keyOnServer}
                  title={keyOnServer ? undefined : 'Save the server API key first'}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add instance
                </Button>
                {keyOnServer && (statusQuery.isFetching ? (
                  <Badge variant="secondary" className="text-[10px]">checking…</Badge>
                ) : statusQuery.isError ? (
                  <Badge variant="destructive" className="text-[10px]">unreachable</Badge>
                ) : state ? (
                  <Badge
                    variant={state === 'open' ? 'default' : state === 'connecting' ? 'secondary' : 'destructive'}
                    className="text-[10px]"
                  >
                    <Plug className="mr-1 h-3 w-3" />
                    {state}
                  </Badge>
                ) : null)}
                {keyOnServer && (
                  <Button variant="ghost" size="sm" onClick={() => void statusQuery.refetch()}>
                    <RefreshCw className={cn('h-3.5 w-3.5', statusQuery.isFetching && 'animate-spin')} />
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            <div className="space-y-1">
              <Label htmlFor="evo-base-url" className="text-xs">Server URL</Label>
              <Input
                id="evo-base-url"
                value={draft.base_url}
                onChange={(e) => setDraft({ base_url: e.target.value })}
                placeholder="https://wa.vid-rrc.com"
                disabled={!canEdit}
                className="h-9 sm:h-9"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="evo-instance" className="text-xs">Instance name</Label>
              <Input
                id="evo-instance"
                value={draft.instance_name}
                onChange={(e) => setDraft({ instance_name: e.target.value })}
                placeholder="main"
                disabled={!canEdit}
                className="h-9 sm:h-9"
              />
              <p className="text-[11px] text-muted-foreground">
                The linked WhatsApp session. Every route ends with this name.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="evo-api-key" className="text-xs">API key</Label>
              <Input
                id="evo-api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={keyOnServer ? `•••• ${initial.evolution.api_key_last4}` : 'Paste the API key'}
                disabled={!canEdit}
                className="h-9 sm:h-9"
              />
              <p className="text-[11px] text-muted-foreground">
                {keyOnServer
                  ? 'A key is saved. Leave blank to keep it, or type a new one to replace it.'
                  : 'Required before anything can send.'}{' '}
                Use the server’s global API key — an instance-scoped token cannot create new
                instances.
              </p>
            </div>

            {statusQuery.isError && (
              <p className="text-xs text-destructive">{getErrorMessage(statusQuery.error)}</p>
            )}
            {state === 'close' && (
              <div className="space-y-2">
                <p className="text-xs text-destructive">
                  The linked phone is logged out. Re-pair the instance before tonight’s run — the
                  sweep will not send while this reads “close”.
                </p>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => setPairing('repair')}>
                    <Plug className="mr-1 h-3.5 w-3.5" />
                    Re-pair now
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-1">
            <CardTitle>Message</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            <Textarea
              value={body}
              onChange={(e) => setDraft({ message_body: e.target.value })}
              disabled={!canEdit}
              rows={4}
              placeholder="Hi {{employee_name}}, you punched in at {{first_punch_in}} on {{date_ist}} but never punched out. Please add your punch-out."
            />

            <div className="flex flex-wrap gap-1">
              {WATI_FIELD_SOURCES.map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => insertToken(`{{${s}}}`)}
                  className="rounded border border-border/60 px-1.5 py-0.5 font-mono text-[10px] transition-colors hover:bg-primary/10 hover:text-primary disabled:opacity-50"
                >
                  {`{{${s}}}`}
                </button>
              ))}
            </div>

            {body.trim() !== '' && (
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground">Preview</p>
                <BodyPreview
                  body={WATI_FIELD_SOURCES.reduce(
                    (acc, s) => acc.split(`{{${s}}}`).join(PREVIEW_VALUES[s]),
                    body,
                  )}
                />
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2">
                <div className="min-w-0">
                  <Label htmlFor="evo-delay" className="text-sm font-medium">Send delay</Label>
                  <p className="text-xs text-muted-foreground">ms of “typing” per message</p>
                </div>
                <Input
                  id="evo-delay"
                  type="number"
                  min={0}
                  max={30000}
                  value={draft.send_delay_ms}
                  onChange={(e) => setDraft({ send_delay_ms: Number(e.target.value) })}
                  disabled={!canEdit}
                  className="h-9 w-24 text-center sm:h-9"
                />
              </div>

              <SettingRow title="Check WhatsApp first" hint="Skip numbers not on WhatsApp">
                <Switch
                  checked={draft.verify_numbers}
                  onChange={(v) => setDraft({ verify_numbers: v })}
                  disabled={!canEdit}
                  aria-label="Check WhatsApp first"
                />
              </SettingRow>
            </div>
          </CardContent>
        </Card>
      </div>

      {pairing && (
        <EvolutionInstanceDialog
          open
          mode={pairing}
          // The saved name, not the draft: the 'close' badge describes the instance the
          // server is actually configured with, and that is what needs re-scanning.
          instanceName={initial.evolution.instance_name}
          onClose={() => setPairing(null)}
        />
      )}
    </div>
  );
}

function Form({ initial }: { initial: WatiConfigResponse }) {
  const queryClient = useQueryClient();
  const { save, saving } = useMessageConfig();
  const { has } = usePermission();
  const canEdit = has(PERMISSIONS.WATI_CONFIG_EDIT);

  const [enabled, setEnabled] = useState(initial.is_enabled);
  const [provider, setProvider] = useState<MessageProvider>(initial.provider);
  const [sendDaily, setSendDaily] = useState(initial.send_daily);
  const [skipSundays, setSkipSundays] = useState(initial.skip_sundays);
  const [runTime, setRunTime] = useState(initial.run_time);
  const [templateName, setTemplateName] = useState(initial.template_name);
  const [mappings, setMappings] = useState<WatiVariableMapping[]>(initial.mappings);
  const [evolution, setEvolution] = useState<EvolutionDraft>({
    base_url: initial.evolution.base_url,
    instance_name: initial.evolution.instance_name,
    message_body: initial.evolution.message_body,
    send_delay_ms: initial.evolution.send_delay_ms,
    verify_numbers: initial.evolution.verify_numbers,
  });
  // Separate from `evolution` because the key is write-only: the server never sends it
  // back, so an empty box means "unchanged", not "clear it".
  const [apiKey, setApiKey] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [tab, setTab] = useState<'wati' | 'evolution' | 'log'>(initial.provider);

  const templatesQuery = useQuery({
    queryKey: ['messages', 'templates'],
    queryFn: () => messageService.listTemplates(),
    enabled: canEdit && initial.is_configured,
  });

  const selectedTemplate = templatesQuery.data?.find((t) => t.name === templateName) ?? null;

  /**
   * Params come from the live template when we have it, so the mapping rows always match
   * what WATI will actually substitute. Falling back to the saved mappings keeps the tab
   * readable while the template list is still loading.
   */
  const params = selectedTemplate?.params ?? mappings.map((m) => ({ name: m.param_name, sample: null }));

  const sameMappings =
    mappings.length === initial.mappings.length &&
    mappings.every((m) => {
      const before = initial.mappings.find((x) => x.param_name === m.param_name);
      return (
        before &&
        before.source_type === m.source_type &&
        before.source_field === m.source_field &&
        before.static_value === m.static_value
      );
    });

  const sameEvolution =
    evolution.base_url === initial.evolution.base_url &&
    evolution.instance_name === initial.evolution.instance_name &&
    evolution.message_body === initial.evolution.message_body &&
    evolution.send_delay_ms === initial.evolution.send_delay_ms &&
    evolution.verify_numbers === initial.evolution.verify_numbers &&
    apiKey.trim() === '';

  const dirty =
    enabled !== initial.is_enabled ||
    provider !== initial.provider ||
    sendDaily !== initial.send_daily ||
    skipSundays !== initial.skip_sundays ||
    runTime !== initial.run_time ||
    templateName !== initial.template_name ||
    !sameMappings ||
    !sameEvolution;

  const validTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(runTime);
  const validDelay =
    Number.isInteger(evolution.send_delay_ms) &&
    evolution.send_delay_ms >= 0 &&
    evolution.send_delay_ms <= 30000;

  const everyParamMapped =
    params.length === 0 ||
    params.every((p) => {
      const m = mappings.find((x) => x.param_name === p.name);
      return m && (m.source_type === 'field' ? Boolean(m.source_field) : Boolean(m.static_value?.trim()));
    });

  // Enabling half-configured is a silent 10 PM failure, so the same bar applies to both
  // transports: WATI needs a template with every variable mapped, WhatsApp Direct needs a
  // reachable instance and something to say.
  const evolutionReady =
    evolution.base_url.trim() !== '' &&
    evolution.instance_name.trim() !== '' &&
    (initial.evolution.api_key_set || apiKey.trim() !== '') &&
    Boolean(evolution.message_body?.trim());

  const providerReady =
    provider === 'wati' ? Boolean(templateName) && everyParamMapped : evolutionReady;

  const canSave = canEdit && dirty && validTime && validDelay && (!enabled || providerReady) && !saving;

  const testSend = useMutation({
    mutationFn: () => messageService.testSend(testPhone),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Test message sent to ${r.to}`);
      // WATI answers HTTP 200 with result:false when it refuses, so a non-ok result is a
      // real failure and must not read as success.
      else toast.error(r.info ?? 'The provider refused the send');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const runNow = useMutation({
    mutationFn: () => messageService.runNow(),
    onSuccess: (r) => {
      if (r.skipped) toast.info(`Nothing ran — ${r.skipped.replace(/_/g, ' ')}`);
      else toast.success(`Sent ${r.sent} · failed ${r.failed} · skipped ${r.skipped_recipients}`);
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function onSave() {
    save({
      is_enabled: enabled,
      provider,
      send_daily: sendDaily,
      skip_sundays: skipSundays,
      run_time: runTime,
      template_name: templateName,
      mappings,
      evolution: {
        base_url: evolution.base_url,
        instance_name: evolution.instance_name,
        message_body: evolution.message_body,
        send_delay_ms: evolution.send_delay_ms,
        verify_numbers: evolution.verify_numbers,
        // Omitted entirely when blank — an untouched field must not blank the saved key.
        ...(apiKey.trim() === '' ? {} : { api_key: apiKey.trim() }),
      },
    });
  }

  /** The saved provider decides what a test send uses, not the tab being looked at. */
  const testDisabled =
    testPhone.trim() === '' ||
    testSend.isPending ||
    (initial.provider === 'wati' ? !initial.template_name : !initial.evolution.message_body?.trim());

  return (
    <div className="space-y-3">
      {/* ── Schedule — provider-agnostic ──────────────────────────────────── */}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-1">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" /> Miss-punch reminder
          </CardTitle>
          {canEdit && (
            <div className="flex items-center gap-2">
              {dirty && <span className="text-[11px] text-muted-foreground">Unsaved changes</span>}
              <Button size="sm" disabled={!canSave} onClick={onSave}>
                {saving ? 'Saving…' : 'Save settings'}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          <p className="text-xs text-muted-foreground">
            Employees whose check-ins and check-outs don’t balance get a WhatsApp reminder. People on
            approved leave, with a pending “add missing punch” request, or with no punches at all are
            never messaged.
          </p>

          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            <SettingRow title="Send reminders" hint="Master switch for the nightly job">
              <Switch checked={enabled} onChange={setEnabled} disabled={!canEdit} aria-label="Send reminders" />
            </SettingRow>

            <SettingRow
              title="Use WATI"
              hint={provider === 'wati' ? 'On — sending via WATI' : 'Off — sending via WhatsApp Direct'}
            >
              <Switch
                checked={provider === 'wati'}
                onChange={(v) => {
                  setProvider(v ? 'wati' : 'evolution');
                  setTab(v ? 'wati' : 'evolution');
                }}
                disabled={!canEdit}
                aria-label="Use WATI"
              />
            </SettingRow>

            <SettingRow title="Daily" hint="Off — the job never runs, on any day">
              <Switch checked={sendDaily} onChange={setSendDaily} disabled={!canEdit} aria-label="Daily" />
            </SettingRow>

            <SettingRow title="Send on Sunday" hint={skipSundays ? 'Off — Sundays are skipped' : 'On — Sundays included'}>
              <Switch
                checked={!skipSundays}
                onChange={(v) => setSkipSundays(!v)}
                disabled={!canEdit}
                aria-label="Send on Sunday"
              />
            </SettingRow>

            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2">
              <div className="min-w-0">
                <Label htmlFor="msg-run-time" className="text-sm font-medium">Send at (IST)</Label>
                <p className={cn('text-xs', validTime ? 'text-muted-foreground' : 'text-destructive')}>
                  {validTime ? '24-hour clock' : 'Use HH:MM, e.g. 22:00'}
                </p>
              </div>
              <Input
                id="msg-run-time"
                value={runTime}
                onChange={(e) => setRunTime(e.target.value)}
                placeholder="22:00"
                disabled={!canEdit}
                className="h-9 w-20 text-center sm:h-9"
              />
            </div>
          </div>

          {enabled && !providerReady && (
            <p className="text-xs text-destructive">
              {provider === 'wati'
                ? 'Pick a template and map every variable before switching reminders on.'
                : 'Fill in the server URL, instance, API key and message before switching reminders on.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Per-provider configuration ────────────────────────────────────── */}
      <Tabs
        tabs={[
          { key: 'wati', label: 'WATI' },
          { key: 'evolution', label: 'WhatsApp Direct' },
          { key: 'log', label: 'Send Log' },
        ]}
        active={tab}
        onChange={(k) => setTab(k as typeof tab)}
      />

      {tab === 'wati' && (
        <WatiPanel
          initial={initial}
          canEdit={canEdit}
          templateName={templateName}
          setTemplateName={setTemplateName}
          mappings={mappings}
          setMappings={setMappings}
          params={params}
          everyParamMapped={everyParamMapped}
          enabled={enabled && provider === 'wati'}
        />
      )}

      {tab === 'evolution' && (
        <EvolutionPanel
          initial={initial}
          canEdit={canEdit}
          draft={evolution}
          setDraft={(patch) => setEvolution((prev) => ({ ...prev, ...patch }))}
          apiKey={apiKey}
          setApiKey={setApiKey}
        />
      )}

      {tab === 'log' && <SendLog />}

      {canEdit && tab !== 'log' && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle>Test</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            <p className="text-xs text-muted-foreground">
              Sends the <span className="font-medium">saved</span> message to one number using
              placeholder values, through the saved provider
              {' '}(<span className="font-medium">{initial.provider === 'wati' ? 'WATI' : 'WhatsApp Direct'}</span>),
              so you can check the wording without waiting for tonight.
            </p>
            <div className="flex flex-wrap gap-2">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="10-digit mobile"
                className="h-9 w-40 sm:h-9"
              />
              <Button variant="outline" size="sm" disabled={testDisabled} onClick={() => testSend.mutate()}>
                <Send className="h-3.5 w-3.5" />
                {testSend.isPending ? 'Sending…' : 'Send test'}
              </Button>
              <Button variant="outline" size="sm" disabled={runNow.isPending} onClick={() => runNow.mutate()}>
                <Play className="h-3.5 w-3.5" />
                {runNow.isPending ? 'Running…' : 'Run sweep now'}
              </Button>
            </div>
            {initial.last_run_at && (
              <p className="text-[11px] text-muted-foreground">
                Last run{' '}
                {new Date(initial.last_run_at).toLocaleString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}{' '}
                — {initial.last_run_summary}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SendLog() {
  const query = useQuery({
    queryKey: ['messages', 'log'],
    queryFn: () => messageService.getLog(50),
  });
  const rows = query.data ?? [];

  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle>Recent sends</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : query.isError ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive">{getErrorMessage(query.error)}</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>Retry</Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing sent yet. Once the nightly job runs, every message shows up here with its result.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/60 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-3 font-medium">Date</th>
                  <th className="py-1.5 pr-3 font-medium">Employee</th>
                  <th className="py-1.5 pr-3 font-medium">Via</th>
                  <th className="py-1.5 pr-3 font-medium">Number</th>
                  <th className="py-1.5 pr-3 font-medium">Status</th>
                  <th className="py-1.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/40 last:border-b-0">
                    <td className="whitespace-nowrap py-1.5 pr-3">{row.attendance_date}</td>
                    <td className="py-1.5 pr-3">
                      {row.employee_name ?? '—'}
                      {row.employee_code && (
                        <span className="ml-1 text-muted-foreground">{row.employee_code}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-muted-foreground">
                      {row.provider === 'wati' ? 'WATI' : 'Direct'}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3">{row.to_mobile ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      <Badge
                        variant={
                          row.status === 'sent' ? 'default' : row.status === 'failed' ? 'destructive' : 'secondary'
                        }
                        className="text-[10px]"
                      >
                        {row.status}
                      </Badge>
                    </td>
                    <td className="py-1.5 text-muted-foreground">{row.error_message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function MessageConfigSection() {
  const { data, isLoading, isError, refetch } = useMessageConfig();

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading message settings…</p>;
  if (isError || !data) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">Could not load message settings.</p>
        <Button variant="outline" size="sm" onClick={refetch}>Retry</Button>
      </div>
    );
  }

  // Keyed so the form's local state re-seeds after a save without a manual sync effect.
  return (
    <Form
      key={`${data.provider}-${data.template_name}-${data.mappings.length}-${data.is_enabled}-${data.evolution.api_key_set}`}
      initial={data}
    />
  );
}
